if (!customElements.get('fbfy-search-content')) {
  customElements.define('fbfy-search-content', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const events = { signal: this.controller.signal };
      this.form = this.closest('form');
      this.input = this.form.elements.q;
      this.drawer = this.closest('search-drawer');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.sequence = 0;
      try {
        const features = JSON.parse(document.getElementById('shopify-features')?.textContent || '{}');
        this.predictiveSupported = features.predictiveSearch !== false;
      } catch { this.predictiveSupported = true; }
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        this.request?.abort();
        this.sequence += 1;
        if (!this.input.value.trim()) return this.setState('idle');
        this.setState('loading');
        this.timer = setTimeout(() => this.search(), 250);
      }, events);
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-search-term]');
        if (!button || !this.contains(button)) return;
        this.input.value = button.dataset.searchTerm;
        this.input.focus();
        this.input.dispatchEvent(new Event('input', { bubbles: true }));
      }, events);
      this.form.addEventListener('reset', () => {
        clearTimeout(this.timer);
        this.request?.abort();
        this.sequence += 1;
        this.setState('idle');
        this.input.focus();
      }, events);
      this.form.addEventListener('submit', event => {
        this.input.value = this.input.value.trim();
        if (!this.input.value) event.preventDefault();
      }, events);
      this.form.addEventListener('keydown', event => this.moveFocus(event), events);
      this.drawer.addEventListener('dialog:after-show', () => {
        this.input.focus();
        this.loadRecent();
      }, events);
      this.rememberCurrent();
      document.addEventListener('click', event => {
        const source = event.target.closest('.fbfy-home-search input[name="q"]');
        if (source) this.openFrom(source);
      }, events);
      document.addEventListener('input', event => {
        if (event.target.matches('.fbfy-home-search input[name="q"]')) this.openFrom(event.target);
      }, events);
      document.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown' && event.target.matches('.fbfy-home-search input[name="q"]')) {
          event.preventDefault();
          this.openFrom(event.target);
        }
      }, events);
    }
    disconnectedCallback() {
      this.sequence += 1;
      this.controller?.abort();
      this.request?.abort();
      this.recentRequest?.abort();
      clearTimeout(this.timer);
      this.controller = null;
    }
    setState(state) {
      ['idle', 'results', 'loading', 'error'].forEach(key => {
        this.querySelector('[data-search-' + key + ']').hidden = key !== state;
      });
      this.results.setAttribute('aria-busy', String(state === 'loading'));
      this.status.textContent = state === 'loading' ? this.dataset.loading : state === 'error' ? this.dataset.error : '';
    }
    moveFocus(event) {
      if (event.isComposing || !['ArrowDown', 'ArrowUp'].includes(event.key) || this.results.hidden) return;
      const links = Array.from(this.results.querySelectorAll('a[href], button[type="submit"]'));
      if (!links.length) return;
      if (event.target === this.input) {
        event.preventDefault();
        (event.key === 'ArrowDown' ? links[0] : links[links.length - 1]).focus();
        return;
      }
      const index = links.indexOf(event.target);
      if (index < 0) return;
      event.preventDefault();
      const next = index + (event.key === 'ArrowDown' ? 1 : -1);
      (links[next] || this.input).focus();
    }
    async openFrom(source) {
      await customElements.whenDefined('search-drawer');
      this.input.value = source.value;
      await this.drawer.show();
      this.input.focus();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    searchUrl(term, predictive) {
      const url = new URL(predictive ? this.dataset.suggestUrl : this.dataset.searchUrl, location.origin);
      const params = { q: term, section_id: this.dataset.sectionId };
      if (predictive) Object.assign(params, { 'resources[type]': 'product,query', 'resources[limit]': '10', 'resources[limit_scope]': 'each' });
      else Object.assign(params, { type: 'product', 'options[prefix]': 'last' });
      url.search = new URLSearchParams(params).toString();
      return url;
    }
    async search() {
      const term = this.input.value.trim();
      if (!term) return this.setState('idle');
      const sequence = this.sequence;
      const request = new AbortController();
      this.request = request;
      this.setState('loading');
      try {
        let response = await fetch(this.searchUrl(term, this.predictiveSupported), { signal: request.signal });
        if (response.status === 417 && this.predictiveSupported) {
          this.predictiveSupported = false;
          response = await fetch(this.searchUrl(term, false), { signal: request.signal });
        }
        if (!response.ok) throw new Error('Search unavailable');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const results = html.querySelector('[data-search-results]');
        if (!results) throw new Error('No search section');
        if (sequence !== this.sequence || term !== this.input.value.trim() || !this.isConnected) return;
        this.results.replaceChildren(...document.importNode(results, true).childNodes);
        this.setState('results');
        this.status.textContent = this.results.querySelector('[data-search-count]')?.textContent || '';
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.sequence) this.setState('error');
      }
    }
    recentIds() {
      try {
        const ids = JSON.parse(sessionStorage.getItem('fbfy:recent-products') || '[]');
        return Array.isArray(ids) ? ids.filter(id => /^\d+$/.test(String(id))).map(String).slice(0, 6) : [];
      } catch { return []; }
    }
    rememberCurrent() {
      const id = this.dataset.currentId;
      if (!/^\d+$/.test(id || '')) return;
      try {
        sessionStorage.setItem('fbfy:recent-products', JSON.stringify([id, ...this.recentIds().filter(value => value !== id)].slice(0, 6)));
      } catch {}
    }
    async loadRecent() {
      const ids = this.recentIds();
      const key = ids.join(',');
      if (!ids.length || this.recentKey === key) return;
      this.recentRequest?.abort();
      this.recentRequest = new AbortController();
      try {
        const url = new URL(this.dataset.searchUrl, location.origin);
        url.search = new URLSearchParams({ type: 'product', q: ids.map(id => 'id:' + id).join(' OR '), section_id: this.dataset.sectionId }).toString();
        const response = await fetch(url, { signal: this.recentRequest.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const template = html.querySelector('template[data-search-recent-template]');
        if (!template) return;
        const cards = Array.from(template.content.querySelectorAll('[data-product-id]')).sort((a, b) => ids.indexOf(a.dataset.productId) - ids.indexOf(b.dataset.productId)).slice(0, 2);
        if (cards.length) this.querySelector('[data-search-recent]').replaceChildren(...cards.map(card => document.importNode(card, true)));
        this.recentKey = key;
      } catch {}
    }
  });
}
